import { sentryInit } from 'apis/sentry';
import { COOKIE_CONSENT_CHANGED, publishActivation } from 'apis/pubsub';
import { getTokens, pickToken } from 'utils/auth';
import { hasRegistry } from 'apis/registry';
import { reportError } from 'apis/sentry';
import { allowedHost } from 'utils/settings';
import {
  checkConsent,
  consentCategories,
  getCookie,
  setCookie,
  deleteCookie,
} from 'utils/cookies';
import { webFeatureConfig } from 'webFeatureConfig';

const DISABLED: string = 'disabled';
const STORAGE_KEY: string = 'igift-agent';

function canUseCookies(): boolean {
  return checkConsent(consentCategories.strictlyNecessaryCookies);
}

function storeValue(value: string): void {
  getTokens().then((tokens) => {
    const { guest, uid, retailUnit } = pickToken(tokens);
    if (!guest && uid && canUseCookies()) {
      setCookie(STORAGE_KEY, value, 7, retailUnit);
    } else {
      deleteCookie(STORAGE_KEY, retailUnit);
    }
  });
}

function handleActiveRegistryCheck(userId: string): Promise<void> {
  return hasRegistry()
    .then((res) => {
      const { __typename } = res.data.activeRegistry;
      if (__typename === 'ActiveRegistryPayload') {
        storeValue(userId);
        publishActivation();
      } else if (__typename === 'NotFoundError') {
        storeValue(DISABLED);
      } else if (__typename === 'SuspendedRegistryError') {
        storeValue(DISABLED);
      } else {
        throw new Error(`Unhandled __typename ${__typename}`);
      }
    })
    .catch((err) => {
      storeValue(DISABLED);
      reportError(err, { context: 'hasRegistry() catch' });
    });
}

function init(): void {
  if (window.location.hostname !== allowedHost) {
    console.warn('[igift] Invalid hostname');
    return;
  }
  if (!canUseCookies()) {
    console.warn('[igift] Insufficient cookie consent');
    return;
  }
  sentryInit();

  getTokens()
    .then((tokens) => {
      const { guest, uid, isFamilyMember } = pickToken(tokens);
      const cachedUserId: string | null = getCookie(STORAGE_KEY);
      const blockedByLoyaltyStatus: boolean = webFeatureConfig.familyFeatureEnabled
        ? Boolean(!guest && !isFamilyMember)
        : false;
      const userCouldHaveRegistry: boolean = Boolean(
        cachedUserId !== DISABLED && !guest && uid && !blockedByLoyaltyStatus
      );

      if (cachedUserId === uid) {
        publishActivation();
      } else if (userCouldHaveRegistry) {
        return handleActiveRegistryCheck(uid);
      } else {
        storeValue(DISABLED);
      }
    })
    .catch((err) => {
      reportError(err, { context: 'getTokens() catch' });
    });

  window.ikea = window.ikea || {};
  window.ikea.giftRegistry = window.ikea.giftRegistry || {};
  if (Object.keys(window.ikea.giftRegistry).length === 0) {
    Object.assign(window.ikea.giftRegistry, {
      enable: () => {
        getTokens().then((tokens) => {
          const { uid } = pickToken(tokens);
          storeValue(uid);
          publishActivation();
        });
      },
      disable: () => {
        storeValue(DISABLED);
      },
    });
  }
}

// Watch for any changes to the user's cookie preferences
var prevConsentStatus = window?.ikea?.cookieConsent?.statusStringified;
window.ikea.pubsub.subscribe(COOKIE_CONSENT_CHANGED, () => {
  if (window.ikea?.cookieConsent?.statusStringified !== prevConsentStatus) {
    prevConsentStatus = window.ikea?.cookieConsent?.statusStringified;
    init();
  }
});

init();
